import { Capacitor } from "@capacitor/core";
import { ScreenOrientation } from "@capacitor/screen-orientation";
import { CapacitorUpdater } from "@capgo/capacitor-updater";
import { getCurrentCulture } from "@utils/common/culture";

export enum UpdateType {
	NONE = 0,
	STORE = 1,
	POPUP = 2,
	OPT = 3,
	AUTOMATIC = 4,
}

export type CheckVersionResult = {
	shouldShowUpdate: boolean;
	shouldForceUpdate: boolean;
	versionToUpdateTo: string | null;
	shouldForceNativeUpdate: boolean;
	shouldAutoDownloadUpdate: boolean;
};

export function isCapacitorPlatform(): boolean {
	return Capacitor.isNativePlatform();
}
/**
 *
 * @param route - pathname (everything after culture/) cannot contain leading /
 * @param doesRouteContainCulture - in case of using window.location.pathname with culture
 * @returns whole redirect url
 */
export function getRedirectOriginUrl(
	route: string,
	doesRouteContainCulture?: boolean
): string {
	const excludeFirstSlash = route[0] === "/" ? route.slice(1) : route;
	const pathname = doesRouteContainCulture
		? excludeFirstSlash
		: `${getCurrentCulture()}/${excludeFirstSlash}`;
	if (isCapacitorPlatform()) {
		return `${WEBPACK_WEBSITE_URL}/${pathname}`;
	}
	return `${window.location.origin}/${pathname}`;
}

/**
 * @param orientation "landscape" | "portrait"
 * @desc locks orientation only if isCapacitorPlatform
 */
export function setCapacitorDeviceOrientation(
	orientation: "landscape" | "portrait"
): void {
	if (isCapacitorPlatform()) {
		ScreenOrientation.lock({ orientation: orientation });
	}
}

export function unlockCapacitorDeviceOrientation(): void {
	if (isCapacitorPlatform()) {
		ScreenOrientation.unlock();
	}
}

export async function getCapacitorVersion(): Promise<null | string> {
	if (!isCapacitorPlatform()) {
		return null;
	}

	const getVersion = await CapacitorUpdater.current();
	if (getVersion.bundle.version === "builtin") {
		//if builtin, get native version set in capacitor.config.ts
		return getVersion.native;
	}
	return getVersion.bundle.version;
}

export async function getCapacitorUpdateType(
	newVersion: string
): Promise<UpdateType> {
	const currentVersion = await getCapacitorVersion();

	if (currentVersion == newVersion || !currentVersion) {
		return UpdateType.NONE;
	}

	const currentVersionSplit = currentVersion.split(".");

	const newVersionSplit = newVersion.split(".");

	if (currentVersionSplit.length < 3 || newVersionSplit.length < 3) {
		console.error("Cap update: Versions not properly formatted");
		return UpdateType.NONE;
	}

	const shouldCheckNativeUpdateForIOS =
		Capacitor.getPlatform() === "ios" && WEBPACK_CAPACITOR_USE_APPLE_STORE;
	const shouldCheckNativeUpdateForAndroid =
		Capacitor.getPlatform() === "android" &&
		WEBPACK_CAPACITOR_USE_ANDROID_STORE;

	const [majorOld, minorOld, patchOld] = currentVersionSplit.map((version) =>
		Number(version)
	);
	const [majorNew, minorNew, patchNew] = newVersionSplit.map((version) =>
		Number(version)
	);

	if (
		majorOld < majorNew &&
		(shouldCheckNativeUpdateForIOS || shouldCheckNativeUpdateForAndroid)
	) {
		return UpdateType.STORE;
	}

	//switch to AUTOMATIC for auto download without popup (minor update)
	//switch to FORCE for dowload with popup (minor update)
	if (minorOld < minorNew && majorOld == majorNew) {
		return UpdateType.AUTOMATIC;
	}

	if (patchOld < patchNew && majorOld == majorNew && minorOld == minorNew) {
		return UpdateType.OPT;
	}

	return UpdateType.NONE;
}

export function getConfigurationForUpdateType(
	type: UpdateType,
	newMobileVersion: string
): CheckVersionResult {
	let result: CheckVersionResult = {
		shouldShowUpdate: false,
		shouldForceUpdate: false,
		versionToUpdateTo: null,
		shouldForceNativeUpdate: false,
		shouldAutoDownloadUpdate: false,
	};

	switch (type) {
		case UpdateType.NONE:
			break;
		case UpdateType.OPT:
			result.shouldShowUpdate = true;
			result.versionToUpdateTo = newMobileVersion;
			break;
		case UpdateType.POPUP:
			result.shouldShowUpdate = true;
			result.versionToUpdateTo = newMobileVersion;
			result.shouldForceUpdate = true;
			break;
		case UpdateType.AUTOMATIC:
			result.shouldShowUpdate = false;
			result.shouldForceUpdate = true;
			result.shouldAutoDownloadUpdate = true;
			result.versionToUpdateTo = newMobileVersion;
			break;
		case UpdateType.STORE:
			result.shouldShowUpdate = true;
			result.shouldForceUpdate = true;
			result.shouldForceNativeUpdate = true;
			break;
	}

	return result;
}
